import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import { Warning, ArrowLeft, Hash, Storefront, Ruler, ShieldCheck, Package, DownloadSimple } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { itApi } from './itApi';
import NumberDedupeCard from './NumberDedupeCard';
import { useAuth, isAdmin } from '@/auth/AuthContext';
import { todayJst } from '@/utils/jstDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * IT資産・備品のデータ点検レポート（読み取り専用）。
 * 足りない項目と直し方の案だけを出す。この画面からはデータを変更しない。
 */
const Card = ({ icon: Icon, title, count, children, testid }) => (
  <section className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={testid}>
    <h2 className="flex items-center gap-2 text-sm font-bold text-zinc-800">
      <Icon size={16} className="text-zinc-400" />{title}
      <Badge variant="outline" className="ml-1 bg-zinc-50 text-[11px]">{count}件</Badge>
    </h2>
    <div className="mt-3">{count === 0
      ? <p className="text-xs text-zinc-400">該当はありません</p>
      : children}</div>
  </section>
);

const Rows = ({ rows, cols, testid }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs" data-testid={testid}>
      <thead>
        <tr className="border-b border-zinc-200 text-left text-[11px] text-zinc-500">
          {cols.map((c) => <th key={c.key} className="pb-1.5 pr-3 font-semibold">{c.header}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.id || i} className="border-b border-zinc-100 align-top"
            data-testid={`${testid}-row-${i}`}>
            {cols.map((c) => <td key={c.key} className="py-1.5 pr-3">{c.cell(r)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function ITDataReport() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [exporting, setExporting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const needsInputOnly = searchParams.get('filter') === 'needs_input';

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const blob = await itApi.dataReportXlsx();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `it_data_report_${todayJst().replace(/-/g, '')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Excelを書き出しました（データは変更していません）');
    } catch (e) {
      toast.error('Excelの書き出しに失敗しました');
    } finally { setExporting(false); }
  };

  useEffect(() => {
    axios.get(`${API}/it/data-report`).then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || '読み込みに失敗しました'));
  }, []);

  if (err) return <p className="p-6 text-sm text-rose-600" data-testid="it-report-error">{err}</p>;
  if (!data) return <p className="p-6 text-sm text-zinc-400" data-testid="it-report-loading">読み込み中…</p>;

  const t = data.totals;
  const app = (r) => <span data-no-translate>{r.app}</span>;
  const nameCell = (r) => (
    <span className="font-medium text-zinc-800" data-no-translate title={r.name || ''}>
      {r.asset_no ? `${r.asset_no} ` : ''}{r.name || '—'}
    </span>
  );

  return (
    <div className="space-y-4 pb-24" data-testid="it-data-report">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">データ点検レポート</h1>
          <p className="mt-0.5 text-xs text-zinc-500">IT資産 {t.it_assets}件・備品 {t.bihin_items}件を点検しました。</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportXlsx} disabled={exporting}
            data-testid="it-report-export-xlsx">
            <DownloadSimple size={14} className="mr-1" />{exporting ? '作成中…' : 'Excelで書き出す'}
          </Button>
          <Button asChild variant="outline" size="sm" data-testid="it-report-back">
            <Link to="/it/assets"><ArrowLeft size={14} className="mr-1" />資産台帳へ</Link>
          </Button>
        </div>
      </div>

      <p className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
        data-testid="it-report-readonly-note">
        <ShieldCheck size={15} className="mt-0.5 shrink-0" />
        <span>
          この画面は<b>データを一切変更しません</b>。下の「案」はまだ適用していません。
          採番カウンターも進めていません（次の番号 {data.next_shared_number_proposal} は案です）。
          反映してよいものを決めていただいてから、別の操作として作ります。
        </span>
      </p>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {Object.entries(t.missing).map(([label, n]) => (
          <div key={label} className="rounded-lg border border-zinc-200 bg-white p-3"
            data-testid={`it-report-missing-${label}`}>
            <p className="text-[11px] text-zinc-500">{label}が未入力</p>
            <p className={`text-xl font-bold ${n ? 'text-amber-700' : 'text-zinc-300'}`}>{n}</p>
          </div>
        ))}
      </div>

      <Card icon={Warning} title="必須項目が未入力のIT資産" count={t.it_incomplete} testid="it-report-incomplete">
        <Rows testid="it-report-incomplete-table" rows={data.it_incomplete} cols={[
          { key: 'name', header: '資産', cell: nameCell },
          { key: 'missing', header: '未入力', cell: (r) => (
            <span className="flex flex-wrap gap-1">
              {r.missing.map((m) => (
                <Badge key={m} variant="outline" className="border-amber-200 bg-amber-50 text-[10px] text-amber-800">{m}</Badge>
              ))}
            </span>
          ) },
        ]} />
      </Card>

      <Card icon={Package} title="必須項目が未入力のIT消耗品" count={t.it_consumable_incomplete || 0} testid="it-report-consumable-incomplete">
        <Rows testid="it-report-consumable-table" rows={data.it_consumable_incomplete || []} cols={[
          { key: 'name', header: '品目', cell: nameCell },
          { key: 'missing', header: '未入力', cell: (r) => (
            <span className="flex flex-wrap gap-1">
              {r.missing.map((m) => (
                <Badge key={m} variant="outline" className="border-amber-200 bg-amber-50 text-[10px] text-amber-800">{m}</Badge>
              ))}
            </span>
          ) },
        ]} />
      </Card>

      {needsInputOnly ? (
        <button type="button" onClick={() => setSearchParams({}, { replace: true })}
          className="text-xs font-semibold text-teal-700 hover:underline"
          data-testid="it-report-show-all">番号・購入先・単位の点検も表示する</button>
      ) : (<>
      <NumberDedupeCard isAdmin={isAdmin(user)} />

      <Card icon={Hash} title="品番の付け方が揃っていない" count={t.number_issues} testid="it-report-numbers">
        <Rows testid="it-report-numbers-table" rows={data.number_issues} cols={[
          { key: 'app', header: 'アプリ', cell: app },
          { key: 'name', header: '対象', cell: (r) => (
            <span data-no-translate>{r.current || '（品番なし）'} — {r.name || '—'}</span>
          ) },
          { key: 'issue', header: '内容', cell: (r) => r.issue },
          { key: 'proposal', header: '案（未適用）', cell: (r) => <span className="text-zinc-500">{r.proposal}</span> },
        ]} />
      </Card>

      <Card icon={Storefront} title="購入先を仕入先マスターに紐付ける" count={t.supplier_to_map} testid="it-report-suppliers">
        <Rows testid="it-report-suppliers-table" rows={data.supplier_mapping} cols={[
          { key: 'name', header: '資産', cell: nameCell },
          { key: 'current', header: '現在の入力', cell: (r) => <span data-no-translate>{r.current}</span> },
          { key: 'proposal', header: '案（未適用）', cell: (r) => <span className="text-zinc-500">{r.proposal}</span> },
        ]} />
      </Card>

      <Card icon={Ruler} title="消耗品の単位（まとめ売り）" count={t.unit_issues} testid="it-report-units">
        <Rows testid="it-report-units-table" rows={data.unit_issues} cols={[
          { key: 'name', header: '品目', cell: nameCell },
          { key: 'unit', header: '現在の単位', cell: (r) => (
            <span data-no-translate>{r.current_unit}（在庫 {r.stock_qty ?? '—'}）</span>
          ) },
          { key: 'proposal', header: '案（未適用）', cell: (r) => <span className="text-zinc-500">{r.proposal}</span> },
        ]} />
      </Card>
      </>)}
    </div>
  );
}
